import React, { useState, useEffect } from "react";
import { num } from "../../../utils/num";
import { Button, Row, Col, Card, Badge } from "react-bootstrap";
import dayjs from "dayjs";
import userAvatar from "../../../Assets/UserPage/avatar.png";
import { resolveImg } from "../../../Api/Api";
import coinIcon from "../../../Assets/Balance/coin.png";
import "./CollapseItem.css";
import AOS from 'aos';
import 'aos/dist/aos.css';

function CollapseItem({ targetData, onClose }) {
  const [targetDetails, setTargetDetails] = useState(null);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: 'ease-out-quart',
      once: true,
      mirror: false,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    if (targetData) {
      setTargetDetails(targetData);
    }
  }, [targetData]);

  if (!targetDetails) {
    return (
      <div className="target-detail-container">
        <div className="text-center text-white">
          <h4>No target data available</h4>
          <Button 
            variant="outline-light" 
            onClick={onClose}
            className="mt-3"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const calculateProgress = (current, total) => {
    if (!total || total === 0) return 0;
    return Math.min((current / total) * 100, 100);
  };

  const getStatusColor = (current, total) => {
    const progress = calculateProgress(current, total);
    if (progress >= 100) return "#01B574"; // Green
    if (progress >= 70) return "#F7B546"; // Orange
    return "#FC155C"; // Red
  };

  const getStatusBadge = (current, total) => {
    const progress = calculateProgress(current, total);
    if (progress >= 100) return { variant: "success", text: "Completed" };
    if (progress >= 70) return { variant: "warning", text: "In Progress" };
    return { variant: "danger", text: "Behind Target" };
  };

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.toString().split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return parseFloat(timeStr) || 0;
  };

  const formatDuration = (startTime, endTime) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = end.diff(start, 'day');
    return `${duration} days`;
  };

  const getDaysRemaining = (endTime) => {
    const end = dayjs(endTime);
    const now = dayjs();
    const remaining = end.diff(now, 'day');
    return remaining > 0 ? remaining : 0;
  };

  const coinsProgress = calculateProgress(targetDetails.achieved_coins || 0, targetDetails.coins);
  const livesProgress = calculateProgress(targetDetails.achieved_lives || 0, targetDetails.lives);
  const hoursProgress = calculateProgress(
    parseTimeToMinutes(targetDetails.achieved_hours),
    parseTimeToMinutes(targetDetails.hours)
  );

  return (
    <div className="target-detail-container">
      {/* Header Section */}
      <div className="target-header">
        <div className="d-flex align-items-center justify-content-between mb-4" data-aos="fade-down" data-aos-delay="100">
          <Button 
            variant="outline-light" 
            onClick={onClose}
            className="back-btn"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            ← Back to Targets
          </Button>
          <div className="target-status-info" data-aos="fade-left" data-aos-delay="200">
            <Badge 
              bg={getStatusBadge(
                (targetDetails.achieved_coins || 0) + (targetDetails.achieved_lives || 0) + parseTimeToMinutes(targetDetails.achieved_hours),
                (targetDetails.coins || 0) + (targetDetails.lives || 0) + parseTimeToMinutes(targetDetails.hours)
              ).variant}
              className="status-badge"
            >
              {getStatusBadge(
                (targetDetails.achieved_coins || 0) + (targetDetails.achieved_lives || 0) + parseTimeToMinutes(targetDetails.achieved_hours),
                (targetDetails.coins || 0) + (targetDetails.lives || 0) + parseTimeToMinutes(targetDetails.hours)
              ).text}
            </Badge>
            <span className="days-remaining">
              {getDaysRemaining(targetDetails.end_time)} days remaining
            </span>
          </div>
        </div>

        {/* User Info Card */}
        <Card className="user-info-card mb-4" data-aos="fade-up" data-aos-delay="300">
          <Card.Body className="d-flex align-items-center">
            <div className="user-avatar-container">
              <img
                src={targetDetails.user?.img ? resolveImg(targetDetails.user.img) : userAvatar}
                alt="User Avatar"
                className="user-avatar"
              />
            </div>
            <div className="user-details ms-4">
              <h3 className="user-name">{targetDetails.user?.name ? `@${targetDetails.user.name}` : "Unknown User"}</h3>
              <p className="user-username">{targetDetails.user?.nick_name || "unknown"}</p>
              <div className="target-timeline">
                <div className="timeline-item">
                  <span className="timeline-label">Duration:</span>
                  <span className="timeline-value">{formatDuration(targetDetails.start_time, targetDetails.end_time)}</span>
                </div>
                <div className="timeline-item">
                  <span className="timeline-label">Period:</span>
                  <span className="timeline-value">
                    {dayjs(targetDetails.start_time).format("MMM DD")} - {dayjs(targetDetails.end_time).format("MMM DD, YYYY")}
                  </span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Progress Section */}
      <div className="progress-section">
        <h4 className="section-title" data-aos="fade-up" data-aos-delay="400">Performance Analytics Dashboard</h4>
        
        <Row className="g-4">
          {/* Coins Progress */}
          <Col md={4} data-aos="fade-up" data-aos-delay="500">
            <Card className="progress-card coins-card">
              <Card.Body>
                <div className="progress-header">
                  <div className="progress-icon coins-icon">
                    <img src={coinIcon} alt="Coins" />
                  </div>
                  <div className="progress-title">
                    <h5>Coins Collected</h5>
                    <p className="progress-subtitle">Revenue Achievement</p>
                  </div>
                </div>
                
                <div className="progress-stats">
                  <div className="current-value">
                    {num(targetDetails.achieved_coins)}
                  </div>
                  <div className="target-value">
                    / {num(targetDetails.coins)}
                  </div>
                </div>
                
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill coins-progress"
                      style={{ 
                        width: `${calculateProgress(targetDetails.achieved_coins, targetDetails.coins)}%`,
                        backgroundColor: getStatusColor(targetDetails.achieved_coins, targetDetails.coins)
                      }}
                    ></div>
                  </div>
                  <span className="progress-percentage">
                    {calculateProgress(targetDetails.achieved_coins, targetDetails.coins).toFixed(1)}%
                  </span>
                </div>
                
                <div className="progress-status">
                  <Badge 
                    bg={getStatusBadge(targetDetails.achieved_coins, targetDetails.coins).variant}
                    className="status-badge-small"
                  >
                    {getStatusBadge(targetDetails.achieved_coins, targetDetails.coins).text}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Lives Progress */}
          <Col md={4} data-aos="fade-up" data-aos-delay="600">
            <Card className="progress-card lives-card">
              <Card.Body>
                <div className="progress-header">
                  <div className="progress-icon lives-icon">
                    <span>❤️</span>
                  </div>
                  <div className="progress-title">
                    <h5>Lives Utilized</h5>
                    <p className="progress-subtitle">Engagement Metrics</p>
                  </div>
                </div>
                
                <div className="progress-stats">
                  <div className="current-value">
                    {(targetDetails.achieved_lives || 0).toLocaleString()}
                  </div>
                  <div className="target-value">
                    / {(targetDetails.lives || 0).toLocaleString()}
                  </div>
                </div>
                
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill lives-progress"
                      style={{ 
                        width: `${calculateProgress(targetDetails.achieved_lives, targetDetails.lives)}%`,
                        backgroundColor: getStatusColor(targetDetails.achieved_lives, targetDetails.lives)
                      }}
                    ></div>
                  </div>
                  <span className="progress-percentage">
                    {calculateProgress(targetDetails.achieved_lives, targetDetails.lives).toFixed(1)}%
                  </span>
                </div>
                
                <div className="progress-status">
                  <Badge 
                    bg={getStatusBadge(targetDetails.achieved_lives, targetDetails.lives).variant}
                    className="status-badge-small"
                  >
                    {getStatusBadge(targetDetails.achieved_lives, targetDetails.lives).text}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Hours Progress */}
          <Col md={4} data-aos="fade-up" data-aos-delay="700">
            <Card className="progress-card hours-card">
              <Card.Body>
                <div className="progress-header">
                  <div className="progress-icon hours-icon">
                    <span>⏱️</span>
                  </div>
                  <div className="progress-title">
                    <h5>Time Investment</h5>
                    <p className="progress-subtitle">Activity Duration</p>
                  </div>
                </div>
                
                <div className="progress-stats">
                  <div className="current-value">
                    {targetDetails.achieved_hours || "00:00"}
                  </div>
                  <div className="target-value">
                    / {targetDetails.hours || "00:00"}
                  </div>
                </div>
                
                <div className="progress-bar-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill hours-progress"
                      style={{ 
                        width: `${calculateProgress(
                          parseTimeToMinutes(targetDetails.achieved_hours),
                          parseTimeToMinutes(targetDetails.hours)
                        )}%`,
                        backgroundColor: getStatusColor(
                          parseTimeToMinutes(targetDetails.achieved_hours),
                          parseTimeToMinutes(targetDetails.hours)
                        )
                      }}
                    ></div>
                  </div>
                  <span className="progress-percentage">
                    {calculateProgress(
                      parseTimeToMinutes(targetDetails.achieved_hours),
                      parseTimeToMinutes(targetDetails.hours)
                    ).toFixed(1)}%
                  </span>
                </div>
                
                <div className="progress-status">
                  <Badge 
                    bg={getStatusBadge(
                      parseTimeToMinutes(targetDetails.achieved_hours),
                      parseTimeToMinutes(targetDetails.hours)
                    ).variant}
                    className="status-badge-small"
                  >
                    {getStatusBadge(
                      parseTimeToMinutes(targetDetails.achieved_hours),
                      parseTimeToMinutes(targetDetails.hours)
                    ).text}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Target Information Section */}
      <div className="settings-section mt-4" data-aos="fade-up" data-aos-delay="800">
        <Card className="settings-card">
          <Card.Header>
            <h5 className="mb-0">Target Information & Timeline</h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-4">
              <Col md={6} data-aos="fade-right" data-aos-delay="900">
                <div className="info-section">
                  <h6 className="info-section-title">Target Period</h6>
                  <div className="summary-item">
                    <span className="summary-label">Start Date:</span>
                    <span className="summary-value">
                      {dayjs(targetDetails.start_time).format("MMM DD, YYYY")}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">End Date:</span>
                    <span className="summary-value">
                      {dayjs(targetDetails.end_time).format("MMM DD, YYYY")}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Duration:</span>
                    <span className="summary-value">
                      {formatDuration(targetDetails.start_time, targetDetails.end_time)}
                    </span>
                  </div>
                </div>
              </Col>
              <Col md={6} data-aos="fade-left" data-aos-delay="900">
                <div className="info-section">
                  <h6 className="info-section-title">Target Details</h6>
                  <div className="summary-item">
                    <span className="summary-label">Created:</span>
                    <span className="summary-value">
                      {targetDetails.created_at ? dayjs(targetDetails.created_at).format("MMM DD, YYYY") : "N/A"}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Target ID:</span>
                    <span className="summary-value">
                      #{targetDetails.id ?? 'N/A'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Status:</span>
                    <Badge 
                      bg={getStatusBadge(
                        (targetDetails.achieved_coins || 0) + (targetDetails.achieved_lives || 0) + parseTimeToMinutes(targetDetails.achieved_hours),
                        (targetDetails.coins || 0) + (targetDetails.lives || 0) + parseTimeToMinutes(targetDetails.hours)
                      ).variant}
                      className="ms-2"
                    >
                      {getStatusBadge(
                        (targetDetails.achieved_coins || 0) + (targetDetails.achieved_lives || 0) + parseTimeToMinutes(targetDetails.achieved_hours),
                        (targetDetails.coins || 0) + (targetDetails.lives || 0) + parseTimeToMinutes(targetDetails.hours)
                      ).text}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

export default CollapseItem;
